import { useCallback, useEffect, useRef, useState } from "react";
import { BehaviorSubject, EMPTY, Subject, debounceTime, distinctUntilChanged, exhaustMap, from, merge, of } from "rxjs";
import { catchError, finalize, map, switchMap, tap } from "rxjs/operators";
import type { Observable } from "rxjs";

export interface ScrollPageResult<T> {
  data?: T[];
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

export interface UseScrollLoadOptions<T, P = Record<string, unknown>> {
  fetcher: (
    params: P & { pageNo: number; pageSize: number },
  ) => Promise<ScrollPageResult<T> | undefined> | Observable<ScrollPageResult<T> | undefined>;
  params: P;
  pageSize?: number;
  enabled?: boolean;
}

export interface UseScrollLoadReturn<T> {
  items: T[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  sentinelRef: (node: HTMLElement | null) => void;
  refresh: () => void;
  removeItem: (predicate: (item: T) => boolean) => void;
}

interface InternalState<T> {
  items: T[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
}

interface ParamSnapshot<P> {
  params: P;
  paramsKey: string;
  pageSize: number;
  enabled: boolean;
}

const DEFAULT_PAGE_SIZE = 20;
const OBSERVER_ROOT_MARGIN = "200px 0px";
const LOAD_MORE_DEBOUNCE_MS = 200;

const sortRecord = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortRecord);
  }
  if (!value || typeof value !== "object") {
    return value;
  }
  const record = value as Record<string, unknown>;
  return Object.keys(record)
    .toSorted()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = sortRecord(record[key]);
      return acc;
    }, {});
};

const serializeParams = (value: unknown) => {
  try {
    return JSON.stringify(sortRecord(value));
  } catch {
    return String(value);
  }
};

export const useScrollLoad = <T, P = Record<string, unknown>>(
  options: UseScrollLoadOptions<T, P>,
): UseScrollLoadReturn<T> => {
  const { fetcher, params, pageSize = DEFAULT_PAGE_SIZE, enabled = true } = options;

  const fetcherRef = useRef(fetcher);

  const [state, setState] = useState<InternalState<T>>({
    items: [],
    loading: enabled,
    loadingMore: false,
    hasMore: true,
    error: null,
  });
  const [sentinelNode, setSentinelNode] = useState<HTMLElement | null>(null);

  const params$Ref = useRef<BehaviorSubject<ParamSnapshot<P>> | null>(null);
  params$Ref.current ??= new BehaviorSubject<ParamSnapshot<P>>({
    params,
    paramsKey: serializeParams(params),
    pageSize,
    enabled,
  });
  const params$ = params$Ref.current;

  const loadMore$Ref = useRef<Subject<void> | null>(null);
  loadMore$Ref.current ??= new Subject<void>();
  const loadMore$ = loadMore$Ref.current;

  const refresh$Ref = useRef<Subject<void> | null>(null);
  refresh$Ref.current ??= new Subject<void>();
  const refresh$ = refresh$Ref.current;

  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    params$.next({
      params,
      paramsKey: serializeParams(params),
      pageSize,
      enabled,
    });
  }, [enabled, pageSize, params, params$]);

  useEffect(() => {
    const subscription = params$
      .pipe(
        distinctUntilChanged(
          (prev, curr) =>
            prev.paramsKey === curr.paramsKey && prev.pageSize === curr.pageSize && prev.enabled === curr.enabled,
        ),
        switchMap((current) => {
          if (!current.enabled) {
            setState((prev) => ({ ...prev, loading: false, loadingMore: false }));
            return EMPTY;
          }

          let nextPageNo = 1;
          let hasMore = true;

          setState({
            items: [],
            loading: true,
            loadingMore: false,
            hasMore: true,
            error: null,
          });

          const loadMoreTrigger$ = loadMore$.pipe(
            debounceTime(LOAD_MORE_DEBOUNCE_MS),
            map(() => "loadMore" as const),
          );
          const refreshTrigger$ = refresh$.pipe(map(() => "refresh" as const));

          return merge(of("initial" as const), loadMoreTrigger$, refreshTrigger$).pipe(
            exhaustMap((triggerType) => {
              if (triggerType === "loadMore" && !hasMore) {
                return EMPTY;
              }

              const requestPageNo = triggerType === "loadMore" ? nextPageNo : 1;
              const isLoadMore = triggerType === "loadMore";

              setState((prev) => ({
                ...prev,
                loading: !isLoadMore,
                loadingMore: isLoadMore,
                error: null,
              }));

              return from(
                fetcherRef.current({
                  ...(current.params as P),
                  pageNo: requestPageNo,
                  pageSize: current.pageSize,
                }),
              ).pipe(
                tap((response) => {
                  const receivedPageNo = response?.pageNo ?? requestPageNo;
                  const total = response?.total ?? 0;
                  const list = response?.data ?? [];

                  nextPageNo = receivedPageNo + 1;
                  hasMore = receivedPageNo * current.pageSize < total;

                  setState((prev) => {
                    const items = isLoadMore ? [...prev.items, ...list] : list;
                    return {
                      ...prev,
                      items,
                      hasMore,
                      error: null,
                    };
                  });
                }),
                catchError((error: unknown) => {
                  const normalized = error instanceof Error ? error : new Error(String(error));
                  setState((prev) => ({ ...prev, error: normalized }));
                  return EMPTY;
                }),
                finalize(() => {
                  setState((prev) => ({
                    ...prev,
                    loading: false,
                    loadingMore: false,
                  }));
                }),
              );
            }),
          );
        }),
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [loadMore$, params$, refresh$]);

  useEffect(() => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!sentinelNode || !enabled) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((entry) => entry.isIntersecting);
        if (isIntersecting) {
          loadMore$.next();
        }
      },
      {
        root: null,
        rootMargin: OBSERVER_ROOT_MARGIN,
        threshold: 0,
      },
    );

    observer.observe(sentinelNode);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [enabled, loadMore$, sentinelNode]);

  const refresh = useCallback(() => {
    refresh$.next();
  }, [refresh$]);

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    setSentinelNode(node);
  }, []);

  const removeItem = useCallback((predicate: (item: T) => boolean) => {
    setState((prev) => ({
      ...prev,
      items: prev.items.filter((item) => !predicate(item)),
    }));
  }, []);

  return {
    items: state.items,
    loading: state.loading,
    loadingMore: state.loadingMore,
    hasMore: state.hasMore,
    error: state.error,
    sentinelRef,
    refresh,
    removeItem,
  };
};
